import {
  keepPreviousData,
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import {
  archiveSchema,
  draftSchema,
  getSchemaRegistry,
  createSchema,
  getSchemaById,
  listSchemas,
  listSchemaVersions,
  publishSchema,
  updateSchema,
} from './crud';
import type { CreateSchemaInput, UpdateSchemaInput } from '@morph-mapper/types';
import type { SchemaListFilters } from '../../types';

export const schemasQueryOptions = (filters: SchemaListFilters = {}) =>
  queryOptions({
    queryKey: ['schemas', filters],
    queryFn: () => listSchemas(filters),
    placeholderData: keepPreviousData,
  });

export const schemaQueryOptions = (schemaId: string) =>
  queryOptions({
    queryKey: ['schemas', 'detail', schemaId],
    queryFn: () => getSchemaById(schemaId),
    enabled: Boolean(schemaId),
  });

export const schemaVersionsQueryOptions = (schemaId: string) =>
  queryOptions({
    queryKey: ['schemas', 'versions', schemaId],
    queryFn: () => listSchemaVersions(schemaId),
    enabled: Boolean(schemaId),
  });

export const schemaRegistryQueryOptions = () =>
  queryOptions({
    queryKey: ['schemas', 'registry'],
    queryFn: () => getSchemaRegistry(),
    staleTime: 5 * 60 * 1000,
  });

export const useSchemasQuery = (filters: SchemaListFilters = {}) => {
  return useQuery(schemasQueryOptions(filters));
};

export const useSchemaQuery = (schemaId: string) => {
  return useQuery(schemaQueryOptions(schemaId));
};

export const useSchemaVersionsQuery = (schemaId: string) => {
  return useQuery(schemaVersionsQueryOptions(schemaId));
};

export const useCreateSchemaMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: CreateSchemaInput) => createSchema(payload),
    onSuccess: async () => {
      queryClient.invalidateQueries({ queryKey: ['schemas'] });
    },
  });
};

export const useUpdateSchemaMutation = (schemaId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: UpdateSchemaInput) => updateSchema(schemaId, payload),
    onSuccess: async (updatedSchema) => {
      queryClient.setQueryData(['schemas', 'detail', schemaId], updatedSchema);
      if (updatedSchema.id !== schemaId) {
        queryClient.setQueryData(
          ['schemas', 'detail', updatedSchema.id],
          updatedSchema,
        );
        queryClient.invalidateQueries({
          queryKey: ['schemas', 'versions', schemaId],
        });
        queryClient.invalidateQueries({
          queryKey: ['schemas', 'versions', updatedSchema.id],
        });
      } else {
        queryClient.invalidateQueries({
          queryKey: ['schemas', 'versions', schemaId],
        });
      }
      queryClient.invalidateQueries({ queryKey: ['schemas'] });
    },
  });
};

export const useArchiveSchemaMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (schemaId: string) => archiveSchema(schemaId),
    onSuccess: async () => {
      queryClient.invalidateQueries({ queryKey: ['schemas'] });
    },
  });
};

export const usePublishSchemaMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (schemaId: string) => publishSchema(schemaId),
    onSuccess: async (updatedSchema) => {
      queryClient.setQueryData(['schemas', 'detail', updatedSchema.id], updatedSchema);
      queryClient.invalidateQueries({ queryKey: ['schemas', 'versions', updatedSchema.id] });
      queryClient.invalidateQueries({ queryKey: ['schemas'] });
    },
  });
};

export const useDraftSchemaMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (schemaId: string) => draftSchema(schemaId),
    onSuccess: async (updatedSchema) => {
      queryClient.setQueryData(['schemas', 'detail', updatedSchema.id], updatedSchema);
      queryClient.invalidateQueries({ queryKey: ['schemas', 'versions', updatedSchema.id] });
      queryClient.invalidateQueries({ queryKey: ['schemas'] });
    },
  });
};

export const useSchemaRegistryQuery = () => {
  return useQuery(schemaRegistryQueryOptions());
};
